import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import LOGO_BASE64 from './logoBase64';
import RUBRICA_BASE64 from './rubricaBase64';
import gerarHashVerificacao from './hashVerificacao';

// ─── Paleta ────────────────────────────────────────────────────────
const AZUL       = [25, 118, 210];
const AZUL_ESC   = [13, 71, 161];
const AZUL_CLARO = [227, 242, 253];
const CINZA      = [60, 60, 60];
const PRETO      = [33, 33, 33];
const CINZA_CLARO = [80, 80, 80];
const VERDE      = [46, 125, 50];
const VERDE_BG   = [232, 245, 233];
const AMBER      = [245, 158, 11];
const AMBER_BG   = [255, 251, 235];
const AMBER_DARK = [120, 53, 15];
const VERMELHO   = [198, 40, 40];

// ─── Layout ────────────────────────────────────────────────────────
const M = 14;
const PAGE_W = 210;
const PAGE_H = 297;
const W = PAGE_W - M * 2; // 182mm
const RODAPE_H = 12;
const LIMITE_Y = PAGE_H - RODAPE_H - 5; // Y máximo antes do rodapé

// ─── Helpers ───────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('pt-BR');
}
function fmtDateTime(d) {
  if (!d) return '-';
  return new Date(d).toLocaleString('pt-BR');
}
function fmtCPF(cpf) {
  if (!cpf) return '-';
  const s = String(cpf).replace(/\D/g, '');
  if (s.length !== 11) return cpf;
  return s.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}
function calcIdade(dn) {
  if (!dn) return null;
  const h = new Date(), n = new Date(dn);
  let i = h.getFullYear() - n.getFullYear();
  const m = h.getMonth() - n.getMonth();
  if (m < 0 || (m === 0 && h.getDate() < n.getDate())) i--;
  return i;
}
function sexoLabel(s) {
  if (s === 'M' || s === 'Masculino') return 'Masculino';
  if (s === 'F' || s === 'Feminino') return 'Feminino';
  return s || '';
}
function limparNome(nome) {
  if (!nome) return '';
  return nome.replace(/[.:]+$/, '').replace(/\.{2,}/g, '').trim();
}
function limparNomeExame(nome) {
  if (!nome) return '';
  let n = nome;
  // Remover sufixos de local (-ITA, -HP, -CAM, -PED, -LAG, -LAGOA)
  n = n.replace(/\s*-\s*(ITA|HP|CAM|PED|LAG|LAGOA)\s*$/i, '');
  // Remover "LAB REDE" e qualquer coisa depois
  n = n.replace(/\s+LAB\s+REDE\b.*$/i, '');
  // Remover material entre parênteses no final (SANGUE, SORO, PLASMA)
  n = n.replace(/\s*\((SANGUE|SORO|PLASMA)\)\s*$/i, '');
  // Limpar espaços e pontuação no final
  n = n.replace(/[\s,\-]+$/, '').trim();
  // Title case: primeira letra de cada palavra em maiúscula
  n = n.toLowerCase().replace(/(^|[\s,/(-])([a-zA-Z])/g, (m, pre, c) => pre + c.toUpperCase());
  return n;
}

// ─── Montar campos do paciente em grid 2x2 ─────────────────────────
function montarCamposPaciente(r) {
  const idade = calcIdade(r.PACIENTE_DATANASCIMENTO);
  const sexo = sexoLabel(r.PACIENTE_SEXO);
  const idadeSexo = [idade !== null ? `${idade} anos` : null, sexo].filter(Boolean).join(' - ');

  const pacCodigo = r.PACIENTE_MATRICULA || r.PACIENTE_IDENTIDADE || '';

  const campos = [];
  campos.push(['Paciente', r.PACIENTE_NOME || '-']);
  campos.push(['Idade / Sexo', idadeSexo || '-']);
  if (pacCodigo) campos.push(['Codigo', pacCodigo]);
  if (r.PACIENTE_CPF) campos.push(['CPF', fmtCPF(r.PACIENTE_CPF)]);
  campos.push(['Data Nasc.', fmtDate(r.PACIENTE_DATANASCIMENTO)]);
  if (r.PACIENTE_TELEFONE) campos.push(['Telefone', r.PACIENTE_TELEFONE]);
  if (r.PACIENTE_EMAIL) campos.push(['E-mail', r.PACIENTE_EMAIL]);

  const endParts = [
    r.PACIENTE_ENDERECO,
    r.PACIENTE_NUMERO ? `N ${r.PACIENTE_NUMERO}` : null,
    r.PACIENTE_COMPLEMENTO, r.PACIENTE_BAIRRO,
    r.PACIENTE_CIDADE && r.PACIENTE_UF ? `${r.PACIENTE_CIDADE}/${r.PACIENTE_UF}` : r.PACIENTE_CIDADE,
    r.PACIENTE_CEP ? `CEP ${r.PACIENTE_CEP}` : null,
  ].filter(Boolean).join(', ');
  if (endParts) campos.push(['Endereco', endParts]);

  campos.push(['Medico(a)', r.MEDICO_NOME || '-']);
  if (r.CRM) campos.push(['CRM', r.CRM + (r.UF_CRM ? '/' + r.UF_CRM : '')]);
  campos.push(['Protocolo', String(r.PROTOCOLO || '-')]);
  campos.push(['Atendimento', String(r.ID)]);
  campos.push(['Data Atend.', fmtDate(r.DATA)]);
  if (r.DATAENTREGA) campos.push(['Prev. Entrega', fmtDate(r.DATAENTREGA)]);
  if (r.CONVENIO_NOME || r.CONVENIO) campos.push(['Convenio', (r.CONVENIO_NOME || r.CONVENIO) + (r.PLANO ? ' / ' + r.PLANO : '')]);

  const linhas = [];
  for (let i = 0; i < campos.length; i += 2) {
    const e = campos[i], d = campos[i + 1];
    linhas.push([e[0], e[1], d ? d[0] : '', d ? d[1] : '']);
  }
  return linhas;
}

// ─── Desenhar tabela do paciente (sem borda — a borda vem do bloco) ─
function desenharTabelaPaciente(doc, linhas, y) {
  autoTable(doc, {
    startY: y,
    margin: { left: M + 2, right: M + 2 },
    head: [], body: linhas, theme: 'plain',
    styles: {
      fontSize: 7, textColor: PRETO, lineWidth: 0,
      cellPadding: { top: 0.5, bottom: 0.5, left: 1.5, right: 1.5 },
    },
    columnStyles: {
      0: { fontStyle: 'bold', textColor: CINZA, cellWidth: 21, fontSize: 6.5 },
      1: { cellWidth: 68 },
      2: { fontStyle: 'bold', textColor: CINZA, cellWidth: 21, fontSize: 6.5 },
      3: { cellWidth: 68 },
    },
    didDrawCell: (data) => {
      if (data.column.index === 1 && data.row.index >= 0) {
        const lbl = data.row.cells[0];
        if (lbl && lbl.raw === 'Protocolo') {
          const { x, y: cy, width, height } = data.cell;
          doc.setFillColor(255, 255, 255);
          doc.rect(x, cy, width, height, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(...AZUL);
          doc.text(data.cell.raw, x + 1.5, cy + height - 0.8);
        }
      }
    },
  });
  return doc.lastAutoTable.finalY;
}

// ─── Cabeçalho COMPLETO (página 1) — lab + paciente dentro de borda ─
function desenharCabecalhoCompleto(doc, r) {
  const yTop = M;

  // Barra azul no topo da borda
  doc.setFillColor(...AZUL);
  doc.rect(M, yTop, W, 1.2, 'F');

  const yS = yTop + 3.5;

  // Logo (centralizada à esquerda)
  try {
    doc.addImage(LOGO_BASE64, 'PNG', M + 3, yS - 2, 45, 20);
  } catch (e) { /* fallback sem logo */ }

  // Endereços à direita (3 unidades)
  const rX = M + W - 3;
  const addrX = M + 52; // após a logo
  let aY = yS;
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');

  // Matriz
  doc.setTextColor(...PRETO);
  doc.setFont('helvetica', 'bold');
  doc.text('Matriz', addrX, aY);
  doc.setFont('helvetica', 'normal');
  doc.text(' - Av. Ribeiro Pena, 301A - Centro - Itapecerica/MG - CEP: 35.550-000', addrX + doc.getTextWidth('Matriz'), aY);
  aY += 2.5;
  doc.setTextColor(...CINZA);
  doc.setFontSize(5);
  doc.text('CRF/MG 42.189    CNES: 0483907', addrX, aY);
  aY += 3.5;

  // Filial Camacho
  doc.setTextColor(...PRETO);
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'bold');
  doc.text('Filial', addrX, aY);
  doc.setFont('helvetica', 'normal');
  doc.text(' - Rua Arlindo Isaias, 07 - Centro - Camacho/MG - CEP: 35.555-000', addrX + doc.getTextWidth('Filial'), aY);
  aY += 2.5;
  doc.setTextColor(...CINZA);
  doc.setFontSize(5);
  doc.text('CRF/MG 44.018    CNES: 3352412', addrX, aY);
  aY += 3.5;

  // Filial Pedra do Indaiá
  doc.setTextColor(...PRETO);
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'bold');
  doc.text('Filial', addrX, aY);
  doc.setFont('helvetica', 'normal');
  doc.text(' - Pca. Gabriel Resende Passos, 60 - Centro - Pedra do Indaia/MG - CEP: 35.565-000', addrX + doc.getTextWidth('Filial'), aY);
  aY += 2.5;
  doc.setTextColor(...CINZA);
  doc.setFontSize(5);
  doc.text('CRF/MG 47.706    CNES: 5810086', addrX, aY);
  aY += 3;

  // Fone
  doc.setTextColor(...PRETO);
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'bold');
  doc.text('Fone / WhatsApp: (37) 3341-1460', addrX, aY);

  // Badges PNCQ / SBAC
  const bW = 12, bH = 5, bGap = 2;
  const bX = rX - bW * 2 - bGap;
  const bY = aY - 3;
  doc.setFillColor(...AZUL);
  doc.roundedRect(bX, bY, bW, bH, 1, 1, 'F');
  doc.roundedRect(bX + bW + bGap, bY, bW, bH, 1, 1, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(5.5);
  doc.setTextColor(255, 255, 255);
  doc.text('PNCQ', bX + bW / 2, bY + 3.5, { align: 'center' });
  doc.text('SBAC', bX + bW + bGap + bW / 2, bY + 3.5, { align: 'center' });

  // Linha separadora lab / paciente
  const sepY = aY + 3;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.2);
  doc.line(M + 2, sepY, M + W - 2, sepY);

  // Tabela paciente
  const yPac = desenharTabelaPaciente(doc, montarCamposPaciente(r), sepY + 1);

  // ── Borda retangular ao redor de tudo ──
  const blocoH = yPac - yTop + 2;
  doc.setDrawColor(190, 190, 190);
  doc.setLineWidth(0.4);
  doc.roundedRect(M, yTop, W, blocoH, 2, 2, 'S');

  return yPac + 6;
}

// ─── Cabeçalho COMPACTO (páginas 2+) — lab resumido + paciente, com borda ─
function desenharCabecalhoCompacto(doc, r) {
  const yTop = M;

  // Barra azul fina
  doc.setFillColor(...AZUL);
  doc.rect(M, yTop, W, 0.8, 'F');

  const yS = yTop + 3;

  // Logo compacta
  try {
    doc.addImage(LOGO_BASE64, 'PNG', M + 3, yS - 1, 30, 13.3);
  } catch (e) { /* fallback sem logo */ }

  // Contato compacto à direita
  const rX = M + W - 3;
  doc.setFontSize(5.5);
  doc.setTextColor(...CINZA);
  doc.setFont('helvetica', 'normal');
  doc.text('(37) 3341-1460 | labsantarita.itapecerica@gmail.com', rX, yS + 2.5, { align: 'right' });

  // Linha separadora
  const sepY = yS + 13;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.15);
  doc.line(M + 2, sepY, M + W - 2, sepY);

  // Tabela paciente
  const yPac = desenharTabelaPaciente(doc, montarCamposPaciente(r), sepY + 0.5);

  // ── Borda ──
  const blocoH = yPac - yTop + 1.5;
  doc.setDrawColor(190, 190, 190);
  doc.setLineWidth(0.3);
  doc.roundedRect(M, yTop, W, blocoH, 1.5, 1.5, 'S');

  return yPac + 5;
}

// Altura estimada do cabeçalho compacto (para cálculos de espaço)
const HEADER_COMPACTO_H = 56;
// Altura do label de continuação
const CONT_LABEL_H = 7;

// ─── Cards resumo ──────────────────────────────────────────────────
function desenharResumo(doc, exames, y) {
  const total = exames.length;
  const liberados = exames.filter(e => exameEstaLiberado(e)).length;
  const pendentes = total - liberados;

  const cardW = 40, cardH = 10, gap = 6;
  const startX = M + (W - (cardW * 3 + gap * 2)) / 2;

  [
    { label: 'Total Exames', value: total, bg: AZUL_CLARO, fg: AZUL_ESC },
    { label: 'Liberados', value: liberados, bg: VERDE_BG, fg: VERDE },
    { label: 'Pendentes', value: pendentes, bg: [255, 243, 224], fg: [230, 81, 0] },
  ].forEach((card, i) => {
    const cx = startX + i * (cardW + gap);
    doc.setFillColor(...card.bg);
    doc.roundedRect(cx, y, cardW, cardH, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...card.fg);
    doc.text(String(card.value), cx + 8, y + 6.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.text(card.label, cx + 17, y + 6.5);
  });
  return y + cardH + 4;
}

// ─── Mini bar chart horizontal ─────────────────────────────────────
function desenharMiniBarras(doc, componentes, y, resultado) {
  const numericos = componentes.filter(c => {
    const val = parseFloat((c.RESULTADO || '').trim().replace(',', '.'));
    if (isNaN(val) || val === 0) return false;
    return !!(c.VALOR_REFERENCIA_COMPONENTE || '').match(/([\d.,]+)\s*a\s*([\d.,]+)/);
  }).slice(0, 8);

  if (numericos.length < 3) return y;

  y = verificarEspaco(doc, y, numericos.length * 5 + 14, resultado);

  y += 2;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...CINZA);
  doc.text('GRAFICO DE RESULTADOS', M, y);
  y += 4;

  const barMaxW = 80, labelW = 50, valW = 20;
  const barX = M + labelW, barH = 3.5, gap = 1.5;

  numericos.forEach(c => {
    const val = parseFloat((c.RESULTADO || '').trim().replace(',', '.'));
    const match = (c.VALOR_REFERENCIA_COMPONENTE || '').match(/([\d.,]+)\s*a\s*([\d.,]+)/);
    const min = parseFloat(match[1].replace(',', '.'));
    const max = parseFloat(match[2].replace(',', '.'));
    const rangeMax = max * 1.5;
    const barW = Math.min(Math.max((val / rangeMax) * barMaxW, 2), barMaxW);
    const ok = val >= min && val <= max;
    const cor = ok ? VERDE : (val > max ? VERMELHO : AMBER);

    const nome = limparNome(c.COMPONENTE_NOME || c.COMPONENTE_LAUDO || '');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...PRETO);
    doc.text(nome.length > 28 ? nome.substring(0, 27) + '..' : nome, M, y + barH - 0.5);

    doc.setFillColor(240, 240, 240);
    doc.rect(barX, y, barMaxW, barH, 'F');
    const rS = (min / rangeMax) * barMaxW;
    doc.setFillColor(220, 237, 220);
    doc.rect(barX + rS, y, ((max - min) / rangeMax) * barMaxW, barH, 'F');
    doc.setFillColor(...cor);
    doc.roundedRect(barX, y, barW, barH, 0.5, 0.5, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    doc.setTextColor(...cor);
    doc.text(String(val), barX + barMaxW + 2, y + barH - 0.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5);
    doc.setTextColor(...CINZA_CLARO);
    doc.text(c.COMPONENTE_UNIDADE || '', barX + barMaxW + valW, y + barH - 0.5);

    y += barH + gap;
  });

  y += 1;
  let legX = M + labelW;
  [
    { label: 'Faixa de referencia', color: [220, 237, 220] },
    { label: 'Normal', color: VERDE },
    { label: 'Alterado', color: VERMELHO },
  ].forEach(item => {
    doc.setFillColor(...item.color);
    doc.rect(legX, y - 1.5, 3, 2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5);
    doc.setTextColor(...CINZA);
    doc.text(item.label, legX + 4, y);
    legX += 4 + doc.getTextWidth(item.label) + 5;
  });

  return y + 4;
}

// ─── Rodapé ────────────────────────────────────────────────────────
function desenharRodape(doc, pag, total) {
  const y = PAGE_H - 8;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.2);
  doc.line(M, y - 2, M + W, y - 2);
  doc.setFontSize(5.5);
  doc.setTextColor(...CINZA_CLARO);
  doc.setFont('helvetica', 'normal');
  doc.text(`Pagina ${pag} de ${total}`, M, y);
  doc.text('SANTA RITA - Analises Clinicas', M + W / 2, y, { align: 'center' });
  doc.text(`Gerado em: ${fmtDateTime(new Date())}`, M + W, y, { align: 'right' });
}

// ─── Label de continuação do exame ─────────────────────────────────
function desenharContinuacao(doc, nomeExame, y, seq) {
  doc.setFillColor(...AZUL_CLARO);
  doc.roundedRect(M, y, W, 5.5, 1, 1, 'F');
  // Mini badge sequencial na continuação
  if (seq) {
    doc.setFillColor(...AZUL);
    doc.roundedRect(M + 1.5, y + 0.5, 4.5, 4.5, 2.2, 2.2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    doc.setTextColor(255, 255, 255);
    doc.text(seq, M + 3.75, y + 3.5, { align: 'center' });
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...AZUL_ESC);
  const textX = seq ? M + 8 : M + 3;
  doc.text(`${nomeExame}  (continuacao)`, textX, y + 3.8);
  return y + 7;
}

// ─── Verificar espaço — quebra página com header + continuação ─────
function verificarEspaco(doc, yAtual, necessario, resultado, nomeExameCont, seq) {
  if (yAtual + necessario > LIMITE_Y) {
    doc.addPage();
    let y = desenharCabecalhoCompacto(doc, resultado);
    if (nomeExameCont) {
      y = desenharContinuacao(doc, nomeExameCont, y, seq);
    }
    return y;
  }
  return yAtual;
}

// ─── Verificar se exame está liberado (baseado em SITUACAO) ──
// D=Digitado, L=Liberado, I=Impresso, E=Entregue, A=Assinado → pronto
// P=Pendente, M=Parcial, C=Coletado, F=Falta conferir → NÃO pronto
function exameEstaLiberado(exame) {
  const s = (exame.SITUACAO || '').trim();
  return s === 'D' || s === 'L' || s === 'I' || s === 'E' || s === 'A';
}

// ─── Filtrar resultados válidos ────────────────────────────────────
function filtrarResultados(exame) {
  return (exame.resultados || []).filter(r => {
    const comp = (r.COMPONENTE_NOME || r.COMPONENTE_LAUDO || '').trim();
    const res = (r.RESULTADO || '').trim();
    // Separadores de seção (--- NOME ---)
    if (r._IS_SEPARATOR) return false;
    if (/^-{3,}\s*.+\s*-{3,}$/.test(comp)) return false;
    // Linhas completamente vazias
    if (!comp && !res && !r.COMPONENTE_UNIDADE) return false;
    if (comp === exame.CODIGO_EXAME && !res) return false;
    if (res === exame.CODIGO_EXAME && !comp) return false;
    if (res === '[TEXTO]' || res === '[IMAGEM]') return false;
    return true;
  });
}

// ─── Estimar altura total de um exame ──────────────────────────────
function estimarAlturaExame(doc, exame) {
  let h = 8; // faixa título (8mm) + hash (4mm) = 12mm total, sobra ~4mm de overlap
  if (exame.METODO || exame.MATERIAL || exame.DATACOLETA) h += 3.5;
  if (exame.LABORATORIO_APOIO) h += 3.5;

  const res = filtrarResultados(exame);
  if (res.length > 0) {
    h += 4 + res.length * 3.5;
    const num = res.filter(c => {
      const v = parseFloat((c.RESULTADO || '').trim().replace(',', '.'));
      if (isNaN(v) || v === 0) return false;
      return !!(c.VALOR_REFERENCIA_COMPONENTE || '').match(/([\d.,]+)\s*a\s*([\d.,]+)/);
    });
    if (num.length >= 3) h += Math.min(num.length, 8) * 5 + 14;
  } else {
    h += 9;
  }

  if (exame.VALOR_REFERENCIA_DETALHADO || exame.VALOR_REFERENCIA) {
    const l = doc.splitTextToSize(exame.VALOR_REFERENCIA_DETALHADO || exame.VALOR_REFERENCIA, W - 6);
    h += l.length * 2.6 + 7;
  }
  if (exame.OBSERVACAO || exame.INFO_C_OBSERVACAO) {
    const l = doc.splitTextToSize(exame.INFO_C_OBSERVACAO || exame.OBSERVACAO, W - 6);
    h += l.length * 2.6 + 7;
  }
  h += 28; // RT section com rubrica + hash
  return h;
}

// ─── Função principal ──────────────────────────────────────────────
export default function gerarLaudoPDF(resultado) {
  const doc = new jsPDF('p', 'mm', 'a4');
  const exames = resultado.exames || [];

  // ── Página 1: cabeçalho completo com borda + resumo ──
  let y = desenharCabecalhoCompleto(doc, resultado);
  y = desenharResumo(doc, exames, y);

  // ── Exames ──
  exames.forEach((exame, idx) => {
    const nomeExame = limparNomeExame(exame.EXAME_NOME || exame.CODIGO_EXAME);
    const alturaExame = estimarAlturaExame(doc, exame);
    const espacoRestante = LIMITE_Y - y;
    const espacoPagCheia = LIMITE_Y - HEADER_COMPACTO_H;

    // Se não cabe no restante OU restam menos de 25% da página (≈3/4 da pag já usada), nova página
    const minimoUtil = espacoPagCheia * 0.25;
    if ((alturaExame > espacoRestante) || (espacoRestante < minimoUtil && alturaExame > 30)) {
      doc.addPage();
      y = desenharCabecalhoCompacto(doc, resultado);
    }

    // Separador entre exames
    if (idx > 0 && y > HEADER_COMPACTO_H + 5) {
      y += 2;
    }

    // ── Faixa do exame com marcador sequencial ──
    const seq = String(idx + 1).padStart(2, '0');
    const lib = exameEstaLiberado(exame);
    const bTxt = lib ? 'LIBERADO' : 'PENDENTE';
    const bBg = lib ? VERDE_BG : [255, 243, 224];
    const bFg = lib ? VERDE : [230, 81, 0];

    // Fundo da faixa do título
    doc.setFillColor(243, 246, 252);
    doc.roundedRect(M, y, W, 8, 1.2, 1.2, 'F');

    // Marcador sequencial (badge circular azul)
    const badgeX = M + 1.5;
    const badgeY = y + 1;
    doc.setFillColor(...AZUL);
    doc.roundedRect(badgeX, badgeY, 6, 6, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    doc.setTextColor(255, 255, 255);
    doc.text(seq, badgeX + 3, badgeY + 4.2, { align: 'center' });

    // Nome do exame
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...AZUL_ESC);
    doc.text(nomeExame, M + 10, y + 5.5);

    // Badge situação à direita
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(5.5);
    const bW = doc.getTextWidth(bTxt) + 4;
    doc.setFillColor(...bBg);
    doc.roundedRect(M + W - bW - 1.5, y + 1.5, bW, 5, 1, 1, 'F');
    doc.setTextColor(...bFg);
    doc.text(bTxt, M + W - bW - 1.5 + 2, y + 5);

    // Hash de verificação do exame
    const exameHash = gerarHashVerificacao(`${resultado.PROTOCOLO}-${exame.CODIGO_EXAME}-${resultado.PACIENTE_ID}`);
    doc.setFont('courier', 'normal');
    doc.setFontSize(5);
    doc.setTextColor(180, 180, 180);
    doc.text(exameHash, M + W - 1.5, y + 8.5, { align: 'right' });

    y += 12;

    // ── Meta ──
    if (exame.METODO || exame.MATERIAL || exame.DATACOLETA) {
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...CINZA);
      const p = [];
      if (exame.METODO) p.push(`Met.: ${exame.METODO}`);
      if (exame.MATERIAL) p.push(`Mat.: ${exame.MATERIAL}`);
      if (exame.DATACOLETA) p.push(`Coleta: ${fmtDate(exame.DATACOLETA)}`);
      doc.text(p.join('   |   '), M, y);
      y += 3.5;
    }
    if (exame.LABORATORIO_APOIO) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(6.5);
      doc.setTextColor(...CINZA);
      doc.text(`Lab. apoio: ${exame.LABORATORIO_APOIO}`, M, y);
      y += 3.5;
    }

    // ── Resultados ──
    const resultadosFiltrados = filtrarResultados(exame);

    if (resultadosFiltrados.length > 0) {
      const grupos = {};
      const grupoOrdem = [];
      resultadosFiltrados.forEach(r => {
        const g = r.AGRUPAMENTO || '_default';
        if (!grupos[g]) { grupos[g] = []; grupoOrdem.push(g); }
        grupos[g].push(r);
      });

      const temRefGeral = !!(exame.VALOR_REFERENCIA_DETALHADO || exame.VALOR_REFERENCIA);
      const temMultiplosGrupos = grupoOrdem.length > 1;

      grupoOrdem.forEach((grupo) => {
        const componentes = grupos[grupo];

        // Separar dados reais de observações (TIPODESCRICAO = campo de obs)
        const componentesDados = componentes.filter(c => {
          const nome = (c.COMPONENTE_NOME || '').trim();
          const res = (c.RESULTADO || '').trim();
          if (!nome && !res) return false;
          if (c.TIPODESCRICAO) return false;
          return true;
        });

        const observacoes = componentes.filter(c => {
          if (!c.TIPODESCRICAO) return false;
          return !!(c.RESULTADO || '').trim();
        });

        if (componentesDados.length === 0 && observacoes.length === 0) return;

        if (grupo !== '_default') {
          y = verificarEspaco(doc, y, 10, resultado, nomeExame, seq);
          doc.setFont('helvetica', 'bolditalic');
          doc.setFontSize(8);
          doc.setTextColor(...PRETO);
          doc.text(grupo, M, y);
          y += 3.5;
        }

        if (componentesDados.length > 0) {
          // Mostrar referência inline quando componentes têm ref própria
          const grupoTemRef = componentesDados.some(c => c.VALOR_REFERENCIA_COMPONENTE);
          const algumComponenteTemRefTotal = componentes.some(c => c.VALOR_REFERENCIA_COMPONENTE && !c.TIPODESCRICAO);
          const mostrarRefInline = grupoTemRef || (temMultiplosGrupos && algumComponenteTemRefTotal);

          const colunas = [
            { header: 'Componente', dataKey: 'componente' },
            { header: 'Resultado', dataKey: 'resultado' },
          ];
          if (mostrarRefInline) {
            colunas.push({ header: 'Valores de Referencia', dataKey: 'referencia' });
          } else {
            colunas.push({ header: 'Unidade', dataKey: 'unidade' });
          }

          const dados = componentesDados.map(comp => {
            let res = (comp.RESULTADO || '-').trim().replace(/\s*\(0\)\s*$/, '');
            const unidade = comp.COMPONENTE_UNIDADE || '';
            const row = {
              componente: limparNome(comp.COMPONENTE_NOME || comp.COMPONENTE_LAUDO || `Cod. ${comp.COMPONENTE}`),
              resultado: mostrarRefInline && unidade ? `${res}  ${unidade}` : res,
            };
            if (mostrarRefInline) {
              row.referencia = comp.VALOR_REFERENCIA_COMPONENTE || '';
            } else {
              row.unidade = unidade;
            }
            return row;
          });

          if (dados.length > 0) {
            y = verificarEspaco(doc, y, Math.min(4 + dados.length * 3.5, 50), resultado, nomeExame, seq);

            const cW = mostrarRefInline
              ? { componente: 48, resultado: 50, referencia: W - 48 - 50 }
              : { componente: 70, resultado: 55, unidade: W - 70 - 55 };

            const contMarginTop = HEADER_COMPACTO_H + CONT_LABEL_H;

            autoTable(doc, {
              startY: y,
              margin: { left: M, right: M, top: contMarginTop },
              columns: colunas,
              body: dados,
              theme: 'grid',
              styles: {
                font: 'courier', fontSize: 7,
                cellPadding: { top: 0.8, bottom: 0.8, left: 1.5, right: 1.5 },
                textColor: PRETO, lineColor: [230, 230, 230], lineWidth: 0.15,
              },
              headStyles: {
                fillColor: AZUL, textColor: [255, 255, 255],
                font: 'helvetica', fontStyle: 'bold', fontSize: 6.5, halign: 'left',
                cellPadding: { top: 1.2, bottom: 1.2, left: 1.5, right: 1.5 },
              },
              columnStyles: {
                componente: { font: 'helvetica', fontStyle: 'bold', fontSize: 7, cellWidth: cW.componente },
                resultado: { fontStyle: 'bold', textColor: AZUL_ESC, fontSize: 8, cellWidth: cW.resultado, halign: 'right' },
                ...(mostrarRefInline
                  ? { referencia: { fontSize: 6, textColor: CINZA_CLARO, cellWidth: cW.referencia } }
                  : { unidade: { fontSize: 6.5, textColor: CINZA, cellWidth: cW.unidade } }),
              },
              alternateRowStyles: { fillColor: [250, 250, 250] },
              didDrawPage: (data) => {
                if (data.pageNumber > 1) {
                  desenharCabecalhoCompacto(doc, resultado);
                  desenharContinuacao(doc, nomeExame, HEADER_COMPACTO_H, seq);
                }
              },
            });

            y = doc.lastAutoTable.finalY + 1.5;
          }
        }

        // Observações da seção (campos com TIPODESCRICAO)
        if (observacoes.length > 0) {
          y += 3; // padding antes das observações
          observacoes.forEach(obs => {
            const nome = (obs.COMPONENTE_NOME || '').trim();
            const res = (obs.RESULTADO || '').trim();
            if (!res) return;
            const txt = nome ? `${nome} ${res}` : res;
            const lin = doc.splitTextToSize(txt, W - 6);
            y = verificarEspaco(doc, y, lin.length * 2.6 + 2, resultado, nomeExame, seq);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6.5);
            doc.setTextColor(...PRETO);
            doc.text(lin, M + 2, y);
            y += lin.length * 2.6 + 1.5;
          });
          y += 1; // padding depois das observações
        }
      });

      // Mini barras (apenas para componentes de dados, sem obs)
      const componentesDadosTotal = resultadosFiltrados.filter(c => !c.TIPODESCRICAO);
      y = desenharMiniBarras(doc, componentesDadosTotal, y, resultado);
    } else {
      doc.setFillColor(255, 243, 224);
      doc.roundedRect(M, y - 1, W, 6, 1, 1, 'F');
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(7);
      doc.setTextColor(230, 81, 0);
      doc.text('Resultado ainda nao disponivel - exame em processamento', M + 3, y + 2.5);
      y += 8;
    }

    // ── Referência geral (suprimir se refs por componente já exibidas inline) ──
    const _resFilt = filtrarResultados(exame);
    const _temRefComp = _resFilt.some(r => r.VALOR_REFERENCIA_COMPONENTE && !r.TIPODESCRICAO);

    if ((exame.VALOR_REFERENCIA_DETALHADO || exame.VALOR_REFERENCIA) && !_temRefComp) {
      const txt = exame.VALOR_REFERENCIA_DETALHADO || exame.VALOR_REFERENCIA;
      const lin = doc.splitTextToSize(txt, W - 6);
      y = verificarEspaco(doc, y, lin.length * 2.8 + 7, resultado, nomeExame, seq);
      const refH = lin.length * 2.6 + 5;
      doc.setFillColor(248, 248, 248);
      doc.roundedRect(M, y - 1, W, refH, 1, 1, 'F');
      doc.setFillColor(...AZUL);
      doc.rect(M, y - 1, 1, refH, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...CINZA);
      doc.text('VALOR DE REFERENCIA', M + 3, y + 1.5);
      y += 3.5;
      doc.setFont('courier', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...PRETO);
      doc.text(lin, M + 3, y);
      y += lin.length * 2.6 + 1.5;
    }

    // ── Notas ──
    if (exame.OBSERVACAO || exame.INFO_C_OBSERVACAO) {
      const txt = exame.INFO_C_OBSERVACAO || exame.OBSERVACAO;
      const lin = doc.splitTextToSize(txt, W - 6);
      y = verificarEspaco(doc, y, lin.length * 2.8 + 7, resultado, nomeExame, seq);
      const nH = lin.length * 2.6 + 5;
      doc.setFillColor(...AMBER_BG);
      doc.roundedRect(M, y - 1, W, nH, 1, 1, 'F');
      doc.setFillColor(...AMBER);
      doc.rect(M, y - 1, 1, nH, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...AMBER_DARK);
      doc.text('NOTAS', M + 3, y + 1.5);
      y += 3.5;
      doc.setFont('courier', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(...AMBER_DARK);
      doc.text(lin, M + 3, y);
      y += lin.length * 2.6 + 1.5;
    }

    // ── Responsável Técnico com rubrica ──
    const rtNome = exame.RESPONSAVEL_NOME ? exame.RESPONSAVEL_NOME.trim() : 'Lucas Pedrosa Gussen';
    const rtCrf = exame.RESPONSAVEL_CRF ? exame.RESPONSAVEL_CRF.trim() : '23038';
    const rtCons = exame.RESPONSAVEL_CONSELHO ? exame.RESPONSAVEL_CONSELHO.trim() : 'CRF';
    const rtUf = exame.RESPONSAVEL_UF ? exame.RESPONSAVEL_UF.trim() : 'MG';
    const rtHash = gerarHashVerificacao(`${resultado.PROTOCOLO}-${rtNome}-${rtCrf}`).toUpperCase();

    y = verificarEspaco(doc, y, 28, resultado, nomeExame, seq);
    y += 4;

    // Rubrica (direita, desenhada ANTES do texto)
    const rubX = M + W - 40;
    try {
      doc.addImage(RUBRICA_BASE64, 'PNG', rubX, y, 30, 13);
    } catch (e) { /* fallback sem rubrica */ }
    y += 14;

    // Nome + CRF (direita, abaixo da rubrica)
    const rtTextX = M + W - 3;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(...PRETO);
    doc.text(rtNome, rtTextX, y, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...CINZA);
    doc.text(`${rtCons} - ${rtUf}    ${rtCrf}`, rtTextX, y + 3, { align: 'right' });

    // RT label + hash (esquerda, mesmo nível)
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...PRETO);
    doc.text(`RT - ${rtNome} - ${rtCons}/${rtUf} n ${rtCrf}`, M, y);
    doc.setFont('courier', 'normal');
    doc.setFontSize(5);
    doc.setTextColor(180, 180, 180);
    doc.text(rtHash, M, y + 3);

    y += 6;
  });

  // ── Disclaimer ──
  const dTxt = 'Atencao! Os resultados anteriores informados sao fornecidos de forma a ajudar o clinico na avaliacao da evolucao do paciente. Porem, devem ser analisados com cautela, levando-se em consideracao os dados clinicos, alimentacao, medicamentos, estados patologicos e fisiologicos, bem como valores de referencia da epoca, uma vez que os mesmos podem sofrer modificacoes devido a mudancas de kits, fabricante, metodologia, etc. Somente seu medico pode interpreta-los corretamente.';
  const dLin = doc.splitTextToSize(dTxt, W - 8);
  const dH = dLin.length * 2.6 + 6;
  y = verificarEspaco(doc, y, dH + 6, resultado);
  y += 4;
  doc.setFillColor(...AMBER_BG);
  doc.setDrawColor(253, 230, 138);
  doc.setLineWidth(0.2);
  doc.roundedRect(M, y - 1, W, dH, 1, 1, 'FD');
  doc.setFillColor(...AMBER);
  doc.rect(M, y - 1, 1.2, dH, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(...AMBER_DARK);
  doc.text(dLin, M + 4, y + 2);

  // ── Numerar páginas ──
  const totalPag = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPag; i++) {
    doc.setPage(i);
    desenharRodape(doc, i, totalPag);
  }

  // ── Abrir PDF ──
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
